/**
 * Dex scan Review & Confirm summary (Build 13a).
 *
 * After a live scan finishes, the user is shown a small "here's what I
 * detected — confirm or edit" step before the run is treated as committed:
 * the detected REGION, VARIANT, and COUNT. This module is the pure builder
 * that turns the session totals into that review summary, so it can be
 * unit-tested without React or a camera.
 *
 * Region is derived deterministically from the dex ids the scan actually
 * read (via [[dexIdToRegion]]) rather than trusting any header text — the
 * same Build 13a principle behind the region-lookup rewrite. When a scan
 * spans more than one region (rare, but possible if the user scrolls past a
 * boundary) we report the region the majority of reads fall in.
 */

import { dexIdToRegion, type OcrRegion } from "./dexIdToRegion";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

export interface DexScanReviewSummary {
  /** The region the scan's dex ids resolve to (majority when mixed). */
  region: OcrRegion;
  /** The locked/most-confident variant, or null if none resolved. */
  variant: PokedexVariant | null;
  /** How many distinct entries the scan would commit. */
  count: number;
  /** The dex ids the count is built from, ascending (for the edit affordance). */
  ids: number[];
}

export interface DexScanReviewInput {
  /** The dex numbers the scan read and would commit. */
  ids: number[];
  /** The variant the session locked to (sticky variant lock), if any. */
  variant: PokedexVariant | null;
}

/**
 * Build the Review & Confirm summary from the scanned dex ids and the locked
 * variant. Pure and total: an empty `ids` yields a zero-count "unknown"
 * region summary, never throws.
 */
export function buildDexScanReviewSummary(
  input: DexScanReviewInput,
): DexScanReviewSummary {
  // Dedupe + sort so the count is distinct entries and the edit list is stable.
  const ids = Array.from(new Set(input.ids.filter(isCommittableId))).sort(
    (a, b) => a - b,
  );
  return {
    region: majorityRegion(ids),
    variant: input.variant,
    count: ids.length,
    ids,
  };
}

function isCommittableId(id: number): boolean {
  return Number.isInteger(id) && id >= 1;
}

/**
 * The region the majority of ids fall in. Ties resolve to the region of the
 * lowest id (deterministic). "unknown" only when there are no ids at all.
 */
function majorityRegion(ids: number[]): OcrRegion {
  if (ids.length === 0) return "unknown";
  const counts = new Map<OcrRegion, number>();
  for (const id of ids) {
    const r = dexIdToRegion(id);
    counts.set(r, (counts.get(r) ?? 0) + 1);
  }
  // ids is ascending, so the first region we'd encounter at the max count is
  // the lowest-id region — iterate ids (not the map) to keep the tiebreak
  // deterministic and independent of Map insertion quirks.
  let best: OcrRegion = dexIdToRegion(ids[0]);
  let bestCount = counts.get(best) ?? 0;
  for (const id of ids) {
    const r = dexIdToRegion(id);
    const c = counts.get(r) ?? 0;
    if (c > bestCount) {
      best = r;
      bestCount = c;
    }
  }
  return best;
}
